import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";
import useUsage from "@/hooks/useUsage";

const STEPS = [
  "Dokument wird gelesen",
  "Wichtige Informationen werden erkannt",
  "Fristen und Beträge werden geprüft",
  "Inhalt wird für dich erklärt",
  "Übersetzung wird erstellt",
];

export default function AnalyzingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentResult, authHeaders, user } = useApp();
  const { bumpAnon, refresh: refreshUsage } = useUsage();
  const [stepIndex, setStepIndex] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const state = location.state;
    if (!state?.files?.length) { navigate("/upload"); return; }
    if (startedRef.current) return;
    startedRef.current = true;

    // Fake progression to keep users informed (backend has single call)
    let idx = 0;
    const tick = setInterval(() => {
      idx = Math.min(idx + 1, STEPS.length - 1);
      setStepIndex(idx);
    }, 3500);

    const form = new FormData();
    state.files.forEach(f => form.append("files", f));
    form.append("target_language", state.target_language || "de");
    form.append("save", user ? "true" : "false");

    axios.post(`${API}/analyze-document`, form, {
      headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      timeout: 180000,
    }).then(r => {
      clearInterval(tick);
      setStepIndex(STEPS.length);
      setCurrentResult(r.data);
      if (user) refreshUsage(); else bumpAnon();
      setTimeout(() => navigate("/result"), 400);
    }).catch(err => {
      clearInterval(tick);
      const msg = err?.response?.data?.detail || "Die KI-Analyse ist momentan nicht verfügbar. Bitte versuche es später erneut.";
      toast.error(msg);
      navigate("/upload");
    });

    return () => clearInterval(tick);
  }, [location.state, navigate, setCurrentResult, authHeaders, user, bumpAnon, refreshUsage]);

  return (
    <div className="px-6 pt-8 pb-8 flex flex-col items-center" data-testid="analyzing-page">
      <div className="mt-6 mb-6 relative">
        <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center animate-pulse">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-slate-900 text-center">Brief wird analysiert …</h2>
      <p className="mt-2 text-slate-500 text-center">Das kann bis zu einer Minute dauern.</p>

      <ul className="mt-8 w-full space-y-3">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={i} className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${done ? "bg-emerald-50 border-emerald-100" : active ? "bg-blue-50 border-blue-100" : "bg-white border-slate-100"}`} data-testid={`step-${i}`}>
              {done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              ) : active ? (
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
              )}
              <span className={`text-sm ${done ? "text-emerald-700" : active ? "text-blue-700 font-medium" : "text-slate-500"}`}>{s}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
